import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import AddProspectSheet from "@/components/AddProspectSheet";
import MagicProspectButton from "@/components/MagicProspectButton";
import ExportModal from "@/components/ExportModal";
import { toast } from "sonner";
import { Plus, Download, LayoutGrid, List, FileUp } from "lucide-react";
import { useOffline } from "@/contexts/OfflineContext";

import { STATUSES, classifyProspect } from "@/components/pipeline/constants";
import { PipelineKanbanView } from "@/components/pipeline/PipelineKanbanView";
import { PipelineListView } from "@/components/pipeline/PipelineListView";

export default function Pipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view") === "list" ? "list" : "kanban";
  const [view, setView] = useState(initialView);

  const [items, setItems] = useState([]);
  const [followups, setFollowups] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [activeStatuses, setActiveStatuses] = useState(new Set(["À contacter"]));
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const [analysis, setAnalysis] = useState(null);
  const [listAnalyzing, setListAnalyzing] = useState(false);

  const { pendingIds } = useOffline();

  const load = async () => {
    const { data } = await api.get("/prospects");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const open = () => setAddOpen(true);
    window.addEventListener("openAddProspect", open);
    return () => window.removeEventListener("openAddProspect", open);
  }, []);

  const changeView = (v) => {
    setView(v);
    const nextParams = new URLSearchParams(searchParams);
    if (v === "list") nextParams.set("view", "list");
    else nextParams.delete("view");
    setSearchParams(nextParams, { replace: true });
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/ai/smart-followup");
      setFollowups(data);
      toast.success("Priorisation Prosperr prête");
    } catch {
      toast.error("Erreur Prosperr");
    } finally {
      setAnalyzing(false);
    }
  };

  const runListAnalysis = async () => {
    setListAnalyzing(true);
    try {
      const { data } = await api.post("/ai/prospects-analysis");
      setAnalysis(data);
      toast.success("Analyse Prosperr prête");
    } catch {
      toast.error("Erreur d'analyse");
    } finally {
      setListAnalyzing(false);
    }
  };

  const byStatus = (s) => items.filter((x) => x.status === s);
  const counts = Object.fromEntries(STATUSES.map((s) => [s, byStatus(s).length]));
  const currentList = STATUSES.flatMap((s) => activeStatuses.has(s) ? byStatus(s) : []);

  const toggleStatus = (s) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) { if (next.size > 1) next.delete(s); }
      else next.add(s);
      return next;
    });
  };

  const grouped = useMemo(() => {
    const g = { qualifie: [], chaud: [], tiede: [], froid: [] };
    items.forEach((p) => g[classifyProspect(p)].push(p));
    return g;
  }, [items]);
  const tempCounts = analysis?.counts || {
    qualifie: grouped.qualifie.length,
    chaud: grouped.chaud.length,
    tiede: grouped.tiede.length,
    froid: grouped.froid.length,
  };

  const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
  const newThisWeek = items.filter((p) => (p.created_at || "") >= weekAgo).length;

  return (
    <AppShell>
      {/* Header — editorial 2026 */}
      <div className="px-5 pt-7 pb-4 pr-16">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.3em] mb-2">Mes prospects</div>
            <h1 className="h-editorial text-[34px] sm:text-[42px] text-[var(--text)] tnum" data-testid="pipeline-title">
              {items.length} <span className="text-zinc-500 font-light">prospect{items.length > 1 ? "s" : ""}</span>
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0 mt-2">
            <div className="flex gap-0 border border-[var(--border)] rounded-lg overflow-hidden" data-testid="view-toggle">
              <button
                onClick={() => changeView("kanban")}
                data-testid="view-kanban"
                aria-label="Vue Kanban"
                className={`w-9 h-9 grid place-items-center transition-colors ${
                  view === "kanban" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--surface-alt)]"
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => changeView("list")}
                data-testid="view-list"
                aria-label="Vue Liste"
                className={`w-9 h-9 grid place-items-center transition-colors border-l border-[var(--border)] ${
                  view === "list" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--surface-alt)]"
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setExportOpen(true)}
              data-testid="export-button"
              className="w-9 h-9 rounded-lg border border-[var(--border)] grid place-items-center active:scale-95 transition-colors text-zinc-400 hover:text-white hover:border-zinc-700"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="hairline-gradient mt-4 -mr-5" />

        {/* Quick action pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
          <Link to="/import" className="hp-btn hp-btn-primary rounded-full whitespace-nowrap">
            <FileUp className="w-4 h-4" />
            <span>Importer</span>
          </Link>
          <button onClick={() => setAddOpen(true)} className="hp-btn hp-btn-secondary rounded-full whitespace-nowrap">
            <Plus className="w-4 h-4" />
            <span>Nouveau</span>
          </button>
        </div>
      </div>

      {view === "kanban" && (
        <PipelineKanbanView
          items={items}
          currentList={currentList}
          counts={counts}
          activeStatuses={activeStatuses}
          toggleStatus={toggleStatus}
          filterOpen={filterOpen}
          setFilterOpen={setFilterOpen}
          setActiveStatuses={setActiveStatuses}
          analyzing={analyzing}
          analyze={analyze}
          followups={followups}
          navigate={navigate}
          pendingIds={pendingIds}
        />
      )}

      {view === "list" && (
        <PipelineListView
          items={items}
          grouped={grouped}
          tempCounts={tempCounts}
          analysis={analysis}
          listAnalyzing={listAnalyzing}
          runListAnalysis={runListAnalysis}
          newThisWeek={newThisWeek}
          navigate={navigate}
          setAddOpen={setAddOpen}
        />
      )}

      <div className="h-20" />

      {/* Add-lead FAB */}
      <button
        onClick={() => setAddOpen(true)}
        data-testid="add-prospect-fab"
        aria-label="Ajouter un lead"
        className="fixed bottom-24 right-5 w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white grid place-items-center active:scale-95 transition-colors z-20 shadow-glow"
      >
        <Plus className="w-6 h-6" strokeWidth={1.8} />
      </button>

      <MagicProspectButton />

      <AddProspectSheet open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} />
    </AppShell>
  );
}
